import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../lib/AuthContext";
import {
  BookmarkSimple, Broadcast, MagnifyingGlass, Bell,
  CircleNotch, ArrowRight, Warning, Clock, Eye,
} from "@phosphor-icons/react";

export default function DashboardOverview() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${window.location.origin}/api/dashboard/overview`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [authFetch]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    if (/^\d{10}$/.test(q)) {
      navigate(`/provider/${q}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><CircleNotch className="w-8 h-8 text-teal animate-spin" /></div>;
  }

  const s = data?.stats || {};
  const activity = data?.activity || [];

  return (
    <>
      <Helmet><title>Dashboard | NPI Lookup</title></Helmet>
      <div className="" data-testid="dashboard-overview">
        {/* Quick search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Quick search — enter NPI number or provider name..."
              className="w-full pl-12 pr-4 py-3 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal shadow-sm"
              data-testid="dashboard-quick-search"
            />
          </div>
        </form>

        {/* Alerts banner */}
        {s.alerts_count > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center justify-between" data-testid="alerts-banner">
            <div className="flex items-center gap-3">
              <Warning className="w-5 h-5 text-amber-600" weight="fill" />
              <p className="text-sm font-medium text-amber-800">
                {s.alerts_count} provider{s.alerts_count > 1 ? "s" : ""} in your monitoring list {s.alerts_count > 1 ? "have" : "has"} changes since your last visit
              </p>
            </div>
            <Link to="/dashboard/monitoring" className="text-sm font-semibold text-amber-700 hover:text-amber-800 flex items-center gap-1">
              View Alerts <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={BookmarkSimple} label="Providers Saved" value={s.saved_count} to="/dashboard/saved" color="text-teal" bg="bg-teal/10" />
          <StatCard icon={Broadcast} label="Providers Monitored" value={s.monitored_count} to="/dashboard/monitoring" color="text-violet-600" bg="bg-violet-50" />
          <StatCard icon={MagnifyingGlass} label="Searches This Month" value={s.searches_month} to="/dashboard/history" color="text-navy" bg="bg-navy/5" />
          <StatCard icon={Bell} label="Active Alerts" value={s.alerts_count} to="/dashboard/monitoring" color="text-red-600" bg="bg-red-50" />
        </div>

        {/* Recent Activity */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-lg text-slate-900">Recent Activity</h2>
            <Link to="/dashboard/history" className="text-sm text-teal font-medium hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {activity.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
              <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-display font-semibold text-slate-900 mb-1">No activity yet</h3>
              <p className="text-sm text-slate-500 mb-4">Search for a provider to get started. Your searches and saves will appear here.</p>
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-teal font-medium hover:underline">
                <MagnifyingGlass className="w-4 h-4" /> Search Providers
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
              {activity.map((item, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      item.type === "alert" ? "bg-red-50" : item.type === "save" ? "bg-teal/10" : "bg-slate-100"
                    }`}>
                      {item.type === "alert" ? <Bell className="w-4 h-4 text-red-500" weight="fill" /> :
                       item.type === "save" ? <BookmarkSimple className="w-4 h-4 text-teal" weight="fill" /> :
                       <MagnifyingGlass className="w-4 h-4 text-slate-500" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 truncate">{item.description}</p>
                      <p className="text-xs text-slate-400">{formatTime(item.timestamp)}</p>
                    </div>
                  </div>
                  {item.npi && (
                    <Link to={`/provider/${item.npi}`} className="text-slate-400 hover:text-teal shrink-0 ml-2">
                      <Eye className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StatCard({ icon: Icon, label, value, to, color, bg }) {
  return (
    <Link to={to} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm hover:border-slate-300 transition-all group" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} weight="fill" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value ?? 0}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </Link>
  );
}

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString();
}
